"use client";

import Link from "next/link";
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import styles from "./PortalDirectory.module.css";
import { usePortalTranslations } from "../i18n/client";

export function PortalDirectoryRail({ title, description, href, children }: { title: string; description: string; href: string; children: ReactNode }) {
  const t = usePortalTranslations();
  const id = useId();
  const track = useRef<HTMLDivElement>(null);
  const animation = useRef<{ frame: number; target: number; snap: string } | null>(null);
  const [scrollable, setScrollable] = useState({ previous: false, next: false });
  const stopScrolling = useCallback(() => {
    if (!animation.current) return;
    cancelAnimationFrame(animation.current.frame);
    if (track.current) track.current.style.scrollSnapType = animation.current.snap;
    animation.current = null;
  }, []);

  useEffect(() => {
    const element = track.current;
    if (!element) return;
    const update = () => setScrollable({ previous: element.scrollLeft > 2, next: element.scrollLeft + element.clientWidth < element.scrollWidth - 2 });
    const observer = new ResizeObserver(update);
    observer.observe(element);
    for (const child of element.children) observer.observe(child);
    element.addEventListener("scroll", update, { passive: true });
    element.addEventListener("pointerdown", stopScrolling, { passive: true });
    element.addEventListener("wheel", stopScrolling, { passive: true });
    element.addEventListener("keydown", stopScrolling);
    update();
    return () => {
      stopScrolling();
      observer.disconnect();
      element.removeEventListener("scroll", update);
      element.removeEventListener("pointerdown", stopScrolling);
      element.removeEventListener("wheel", stopScrolling);
      element.removeEventListener("keydown", stopScrolling);
    };
  }, [children, stopScrolling]);

  function move(direction: number) {
    const element = track.current;
    if (!element) return;
    const card = element.firstElementChild;
    if (!card) return;
    const stride = card.getBoundingClientRect().width + parseFloat(getComputedStyle(element).columnGap);
    const visibleCards = Math.max(1, Math.floor(element.clientWidth / stride));
    const position = animation.current?.target ?? element.scrollLeft;
    const nextCard = Math.round(position / stride) + direction * visibleCards;
    const target = Math.max(0, Math.min(nextCard * stride, element.scrollWidth - element.clientWidth));
    stopScrolling();

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      element.scrollTo({ left: target, behavior: "instant" });
      return;
    }

    const start = element.scrollLeft;
    const startedAt = performance.now();
    const motion = { frame: 0, target, snap: element.style.scrollSnapType };
    animation.current = motion;
    element.style.scrollSnapType = "none";
    const step = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / 480);
      const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
      element.scrollTo({ left: start + (target - start) * eased, behavior: "instant" });
      if (progress < 1) motion.frame = requestAnimationFrame(step);
      else stopScrolling();
    };
    motion.frame = requestAnimationFrame(step);
  }

  return <section className={styles.rail} aria-labelledby={`${id}-title`}>
    <div className={styles.railHeading}>
      <div><h2 id={`${id}-title`}><Link href={href} prefetch={false}>{title}<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg></Link></h2><p>{description}</p></div>
      <div className={styles.railControls}>
        <button type="button" aria-label={t("directory.previousPortals", { title })} aria-controls={id} disabled={!scrollable.previous} onClick={() => move(-1)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg></button>
        <button type="button" aria-label={t("directory.nextPortals", { title })} aria-controls={id} disabled={!scrollable.next} onClick={() => move(1)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg></button>
      </div>
    </div>
    <div className={styles.railViewport}>
      <div className={styles.railTrack} id={id} ref={track} tabIndex={0} role="group" aria-label={t("directory.portalGroup", { title })}>{children}</div>
    </div>
  </section>;
}
